import { createServerFn } from "@tanstack/react-start";
import { getRequest, getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resolveApproxGeo, jitter } from "./user-location.geo";

/**
 * Reads approximate location from Cloudflare edge geolocation headers
 * (or the request.cf object) and stores a fuzzed lat/lng on the profile.
 * Falls back to a public IP geolocation lookup when edge headers are missing.
 * No exact address / IP is stored.
 */
export const updateMyApproxLocation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    // Short-circuit: if we already have a fresh (< 24h) location, don't hit
    // the geo provider again. Keeps client-side retry loops cheap.
    // We still touch `approx_updated_at` so it acts as a "last visit" marker
    // for the /mapa 24h feed.
    const { data: existing } = await context.supabase
      .from("profiles")
      .select("approx_latitude, approx_longitude, approx_country, approx_updated_at")
      .eq("id", context.userId)
      .maybeSingle();
    if (
      existing?.approx_latitude != null &&
      existing?.approx_longitude != null &&
      existing?.approx_updated_at &&
      Date.now() - new Date(existing.approx_updated_at).getTime() < 24 * 60 * 60 * 1000
    ) {
      await context.supabase
        .from("profiles")
        .update({ approx_updated_at: new Date().toISOString() })
        .eq("id", context.userId);
      return { ok: true as const, country: existing.approx_country, source: "cached" as const };
    }


    const req = getRequest() as Request & {
      cf?: { latitude?: string; longitude?: string; country?: string };
    };

    const headerLookup = (name: string) => getRequestHeader(name) ?? null;
    const geo = await resolveApproxGeo({
      cf: req.cf,
      getHeader: headerLookup,
      fetchImpl: fetch,
    });

    if (!geo.ok) {
      console.warn("[updateMyApproxLocation] no geo for user", context.userId, {
        hasCf: !!req.cf,
        reason: geo.reason,
      });
      return { ok: false as const, reason: geo.reason };
    }

    const { error } = await context.supabase
      .from("profiles")
      .update({
        approx_latitude: jitter(geo.latitude),
        approx_longitude: jitter(geo.longitude),
        approx_country: geo.country,
        approx_updated_at: new Date().toISOString(),
      })
      .eq("id", context.userId);
    if (error) throw error;
    console.log("[updateMyApproxLocation] updated", context.userId, {
      source: geo.source,
      country: geo.country,
    });
    return { ok: true as const, country: geo.country, source: geo.source };
  });

export type PublicUserLocation = {
  user_id: string;
  latitude: number;
  longitude: number;
  country: string | null;
};

export type MyApproxLocationStatus = {
  signedIn: true;
  hasCoords: boolean;
  country: string | null;
  updatedAt: string | null;
  showOnMap: boolean;
  fresh: boolean; // updated within the last 24h
};

/** Returns whether the current user's map coordinates are populated & fresh. */
export const getMyApproxLocationStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyApproxLocationStatus> => {
    const { data, error } = await context.supabase
      .from("profiles")
      .select("approx_latitude, approx_longitude, approx_country, approx_updated_at, show_on_map")
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw error;
    const hasCoords = data?.approx_latitude != null && data?.approx_longitude != null;
    const fresh =
      hasCoords &&
      !!data?.approx_updated_at &&
      Date.now() - new Date(data.approx_updated_at).getTime() < 24 * 60 * 60 * 1000;
    return {
      signedIn: true,
      hasCoords,
      country: data?.approx_country ?? null,
      updatedAt: data?.approx_updated_at ?? null,
      showOnMap: !!data?.show_on_map,
      fresh,
    };
  });

/**
 * Public: returns anonymized pins only (no names, no contact info).
 * The underlying SECURITY DEFINER RPC is not exposed to anon; we invoke it
 * server-side with the service role and only ever return the safe columns.
 */
export const listPublicUserLocations = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("get_public_user_locations");
  if (error) throw error;
  return (data ?? []) as PublicUserLocation[];
});

/**
 * Public: returns anonymized pins for registered members who do NOT own any
 * business. These users are candidates for building referral structures,
 * recommending businesses and rating visited venues.
 */
export const listMemberLocationsWithoutBusiness = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("get_public_user_locations");
  if (error) throw error;

  const { data: owners, error: ownerErr } = await supabaseAdmin
    .from("businesses")
    .select("owner_id")
    .not("owner_id", "is", null);
  if (ownerErr) throw ownerErr;

  const ownerIds = new Set((owners ?? []).map((o) => o.owner_id as string));
  return ((data ?? []) as PublicUserLocation[]).filter((u) => !ownerIds.has(u.user_id));
});


